import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Box,
  Tooltip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Lancamento } from '../services/api';
import { colors } from '../theme/expertiseTheme';

/**
 * Componente de tabela de lançamentos
 */
type Props = {
  lancamentos: Lancamento[];
};

const TabelaLancamentos: React.FC<Props> = ({ lancamentos }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  if (!lancamentos || lancamentos.length === 0) {
    return null;
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatarValor = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(Math.abs(valor));
  };

  const formatarData = (data: string) => {
    return new Date(`${data}T00:00:00`).toLocaleDateString('pt-BR');
  };

  const lancamentosPaginados = lancamentos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 3,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <Box 
        sx={{ 
          p: 3, 
          pb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: colors.dark,
            }}
          >
            Lançamentos
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.text.secondary,
              mt: 0.5,
            }}
          >
            {lancamentos.length} {lancamentos.length === 1 ? 'item' : 'itens'}
          </Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: colors.text.secondary }}>
                PESSOA
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: colors.text.secondary }}>
                CHAVE PIX
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                VALOR
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                QTD
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: colors.text.secondary }}>
                VENCIMENTO
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: colors.text.secondary }}>
                CONTA
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lancamentosPaginados.map((lanc, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:hover': { 
                    backgroundColor: 'rgba(0, 99, 0, 0.02)',
                  },
                  '&:last-child td': {
                    borderBottom: 0,
                  },
                }}
              >
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: colors.dark,
                    }}
                  >
                    {lanc.pessoa}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: colors.text.light,
                    }}
                  >
                    ID: {lanc.pessoaId}
                  </Typography>
                </TableCell>

                <TableCell>
                  {lanc.chavePix ? (
                    <Tooltip title={lanc.chavePix} arrow>
                      <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label={
                          lanc.chavePix.length > 20
                            ? `${lanc.chavePix.substring(0, 18)}...`
                            : lanc.chavePix
                        }
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0, 99, 0, 0.1)',
                          color: colors.primary,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          '& .MuiChip-icon': {
                            color: colors.primary,
                          },
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Chip
                      icon={<CancelIcon sx={{ fontSize: 16 }} />}
                      label="Sem PIX"
                      size="small"
                      sx={{
                        backgroundColor: 'rgba(220, 38, 38, 0.1)',
                        color: colors.error,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        '& .MuiChip-icon': {
                          color: colors.error,
                        },
                      }}
                    />
                  )}
                </TableCell>

                <TableCell align="right">
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: colors.primary,
                    }}
                  >
                    {formatarValor(lanc.valorTotal)}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Chip
                    label={lanc.quantidadeLancamentos}
                    size="small"
                    sx={{
                      backgroundColor: colors.light,
                      color: colors.text.primary,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      minWidth: 32,
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{ color: colors.text.primary }}
                  >
                    {formatarData(lanc.dataVencimentoMaisProxima)}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: colors.text.secondary,
                      fontSize: '0.8125rem',
                    }}
                  >
                    {lanc.conta}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  {lanc.chavePix ? (
                    <CheckCircleIcon 
                      sx={{ 
                        color: colors.primary,
                        fontSize: 20,
                      }} 
                    />
                  ) : (
                    <CancelIcon 
                      sx={{ 
                        color: colors.error,
                        fontSize: 20,
                      }} 
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${colors.border}`,
        }}
      >
        <TablePagination
          component="div"
          count={lancamentos.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 15, 25, 50]}
          labelRowsPerPage="Mostrando"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
              color: colors.text.secondary,
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default TabelaLancamentos;
